// TEMPORARY FEATURE: drawn overlay images (click-placed curves) for the plane overlay instance.
// Everything lives in this folder plus the CurveDrawingImage node (@cm/template-nodes). To remove the feature, delete this folder,
// the <cm-overlay-drawing-launcher> hook in mattress-component-instance-inspector, and stub the node (see curve-drawing-image.ts).

import {ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output} from "@angular/core"
import {MatDialog, MatDialogRef} from "@angular/material/dialog"
import {ButtonComponent} from "@app/common/components/buttons/button/button.component"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {CurveDrawingImage, ImageInfo} from "@cm/template-nodes"
import type {CurveDrawingImageParameters, InterfaceDescriptor} from "@cm/template-nodes"
import {PLANE_OVERLAY_INSTANCE_ID, unwrapPrefixFromInterfaceId} from "@cm/mattress-nodes"
import type {MattressComponentInstance} from "@cm/mattress-nodes"
import {InspectorSectionComponent} from "@template-editor/components/inspectors/inspector-section/inspector-section.component"
import {
    OverlayDrawingDialogComponent,
    type OverlayDrawingDialogData,
} from "@template-editor/components/inspectors/mattress-inspector/components/overlay-drawing/overlay-drawing-dialog.component"
import {
    overlayFaceSizeCm,
    resizeCurveDrawing,
} from "@template-editor/components/inspectors/mattress-inspector/components/overlay-drawing/overlay-drawing-helpers"
import type {MattressSize} from "@template-editor/components/inspectors/mattress-inspector/components/overlay-drawing/overlay-drawing-helpers"

const defaultParameters: Omit<CurveDrawingImageParameters, "name"> = {
    widthCm: 100,
    heightCm: 100,
    resolution: 1024,
    strokeWidthCm: 0.5,
    color: [0, 0, 0],
    strokes: [],
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-overlay-drawing-launcher",
    templateUrl: "./overlay-drawing-launcher.component.html",
    imports: [InspectorSectionComponent, ButtonComponent],
})
export class OverlayDrawingLauncherComponent {
    private readonly dialog = inject(MatDialog)
    private readonly $can = inject(PermissionsService).$to

    readonly $instance = input.required<MattressComponentInstance>({alias: "instance"})
    readonly $templateDescriptors = input.required<InterfaceDescriptor<unknown>[]>({alias: "templateDescriptors"})
    readonly $wrappedInstanceId = input<string | undefined>(undefined, {alias: "wrappedInstanceId"})
    readonly $mattressSize = input<MattressSize | undefined>(undefined, {alias: "mattressSize"})

    readonly updateParameter = output<{key: string; value: CurveDrawingImage}>()

    private dialogRef: MatDialogRef<OverlayDrawingDialogComponent> | undefined

    constructor() {
        inject(DestroyRef).onDestroy(() => this.dialogRef?.close())
    }

    readonly $imageInputs = computed(() => {
        if (this.$instance().parameters.id !== PLANE_OVERLAY_INSTANCE_ID) return []
        if (!this.$can().read.menu("advancedTemplateEditor")) return []

        const wrappedInstanceId = this.$wrappedInstanceId()
        return this.$templateDescriptors()
            .filter((descriptor) => descriptor instanceof ImageInfo && descriptor.props.type === "input")
            .map((descriptor) => ({
                key: wrappedInstanceId ? unwrapPrefixFromInterfaceId(wrappedInstanceId, descriptor.props.id) : descriptor.props.id,
                label: descriptor.props.name,
            }))
    })

    draw(key: string, label: string) {
        const currentValue = this.$instance().parameters.parameters[key]
        const mattressSize = this.$mattressSize()
        // the face size always follows the current mattress, also for drawings made before it was resized
        const faceSize = mattressSize && (overlayFaceSizeCm(label, mattressSize) ?? overlayFaceSizeCm(key, mattressSize))
        const storedParameters = currentValue instanceof CurveDrawingImage ? currentValue.parameters : {name: `Drawing (${label})`, ...defaultParameters}
        const parameters = faceSize ? resizeCurveDrawing(storedParameters, faceSize.widthCm, faceSize.heightCm) : storedParameters
        // the other inputs' applied drawings, which the dialog can copy from
        const sources = this.$imageInputs()
            .filter((imageInput) => imageInput.key !== key)
            .flatMap((imageInput) => {
                const value = this.$instance().parameters.parameters[imageInput.key]
                return value instanceof CurveDrawingImage && value.parameters.strokes.length > 0
                    ? [{label: imageInput.label, parameters: value.parameters}]
                    : []
            })

        // the apply callback emits through this component, so the dialog must not outlive it
        this.dialogRef?.close()
        this.dialogRef = this.dialog.open<OverlayDrawingDialogComponent, OverlayDrawingDialogData>(OverlayDrawingDialogComponent, {
            data: {
                title: label,
                parameters,
                sizeFromMattress: faceSize !== undefined,
                sources,
                apply: (newParameters) => this.updateParameter.emit({key, value: new CurveDrawingImage({parameters: newParameters})}),
            },
            width: "95vw",
            height: "95vh",
            maxWidth: "95vw",
            // a stray backdrop click or Escape would silently discard unapplied strokes
            disableClose: true,
        })
    }
}
